import { useSponsorship } from 'mednet-cns/src/hooks/sponsorship'
import { TextInput } from 'pharmacy/src/input/textInput'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { ContainedSelect } from 'pharmacy/src/input/select'

import classNames from 'classnames'

import { Button, ButtonCheckbox } from 'pharmacy/src/input/button'

import StarLoader from 'pharmacy/src/misc/loaders/starLoader/starLoader'

import { Label1, Subtitle3 } from 'pharmacy/src/typography'

import {
  getClickedContentId,
  getV2NotificationRedirectUrl,
} from 'mednet-util/src/v2NotificationRedirect'
import { V2_NOTIFICATION_REDIRECT_CLICK_TYPE } from 'mednet-util/src/constants/v2NotificationRedirect'

import * as css from './dynamic.scss'

const SPONSORSHIP_LINK_TYPES = {
  ASK_QUESTION: 'ASK_QUESTION',
  VIEW_PROGRAM_ABSTRACTS: 'VIEW_PROGRAM_ABSTRACTS',
  VIEW_PROGRAM_VIDEOS: 'VIEW_PROGRAM_VIDEOS',
  START_CME_PROGRAM: 'START_CME_PROGRAM',
  VIEW_PROGRAM: 'VIEW_PROGRAM',
}

const SPONSORSHIP_LINK_TYPES_LABELS = {
  [SPONSORSHIP_LINK_TYPES.ASK_QUESTION]: 'Ask Question About Program',
  [SPONSORSHIP_LINK_TYPES.VIEW_PROGRAM_ABSTRACTS]: 'View Program Abstract(s)',
  [SPONSORSHIP_LINK_TYPES.VIEW_PROGRAM_VIDEOS]: 'View Program Video(s)',
  [SPONSORSHIP_LINK_TYPES.START_CME_PROGRAM]: 'Start CME Program',
  [SPONSORSHIP_LINK_TYPES.VIEW_PROGRAM]:
    'View Program - Link will not scroll down to content and users will see the program header',
}

export const SponsorshipTemplateOptions = ({
  sponsorshipTemplateOptions,
  setSponsorshipTemplateOptions,
}) => {
  const [sponsorshipId, setSponsorshipId] = useState()
  const [submittedSponsorshipId, setSubmittedSponsorshipId] = useState()
  const [sponsorship, sponsorshipRequestDetails] = useSponsorship(
    submittedSponsorshipId
  )

  const urlSponsorshipId = useMemo(() => {
    if (typeof window === 'undefined') {
      return undefined
    }
    const url = new URL(window.location.href)
    const searchParams = new URLSearchParams(url.search)
    const sponsorshipIdStr = searchParams.get('sponsorshipId')

    if (sponsorshipIdStr?.trim() && !isNaN(Number(sponsorshipIdStr))) {
      return Number(sponsorshipIdStr)
    }

    return undefined
  }, [])

  useEffect(() => {
    if (urlSponsorshipId) {
      setSponsorshipId(urlSponsorshipId)
    }
  }, [urlSponsorshipId])

  const sponsorshipReady =
    sponsorship &&
    Number(sponsorship?.sponsorshipId ?? '') === Number(sponsorshipId ?? '') &&
    Number(sponsorship?.sponsorshipId ?? '') ===
      Number(submittedSponsorshipId ?? '')

  useEffect(() => {
    if (sponsorshipReady) {
      setSponsorshipTemplateOptions({
        sponsorship,
        linkType: SPONSORSHIP_LINK_TYPES.ASK_QUESTION,
        linkText: 'Ask a question here',
        askQuestionProps: {
          isAnonymous: false,
          isButton: true,
        },
      })
    } else {
      setSponsorshipTemplateOptions({})
    }
  }, [sponsorshipReady, sponsorshipId])

  const setSelectedType = useCallback(
    (type) => {
      setSponsorshipTemplateOptions({
        sponsorship,
        linkType: type,
        linkText:
          type === SPONSORSHIP_LINK_TYPES.ASK_QUESTION
            ? 'Ask a question here'
            : sponsorship?.publicName,
        askQuestionProps: {
          isAnonymous: false,
          isButton: true,
        },
      })
    },
    [setSponsorshipTemplateOptions, sponsorship]
  )

  return (
    <div>
      <div className={classNames(css.settingsRow, css.sponsorshipSettingsRow)}>
        <TextInput
          showClear={false}
          className={classNames(css.input, css.sponsorshipIdInput)}
          type="none"
          value={sponsorshipId}
          onChange={(newValue) => {
            setSponsorshipId(Number(newValue))
            setSubmittedSponsorshipId(undefined)
          }}
          placeholder="Type sponsorship ID here"
        />
        <Button
          className={css.button}
          size="small"
          onClick={() => {
            setSubmittedSponsorshipId(sponsorshipId)
          }}
          isLoading={sponsorshipRequestDetails.isLoading}
          isDisabled={sponsorshipRequestDetails.isLoading || !sponsorshipId}
        >
          Load Sponsorship Details
        </Button>
      </div>
      {sponsorshipRequestDetails.isLoading && (
        <div className={css.loaderContainer}>
          <StarLoader size="small" />
        </div>
      )}
      {sponsorshipReady && (
        <SponsorshipTemplateTypeSelect
          selectedType={sponsorshipTemplateOptions.linkType}
          setSelectedType={setSelectedType}
          cmeModuleAvailable={Boolean(sponsorship?.cmeModule)}
        />
      )}
      {sponsorshipReady &&
        sponsorshipTemplateOptions.linkType ===
          SPONSORSHIP_LINK_TYPES.ASK_QUESTION && (
          <>
            <div
              className={classNames(
                css.settingsRow,
                css.sponsorshipSettingsRow
              )}
            >
              <Label1 className={css.tagSelectLabel}>
                Ask question anonymously:
              </Label1>
              <ButtonCheckbox
                buttonProps={{ size: 'small' }}
                selectText=""
                unselectText=""
                inputProps={{
                  checked:
                    sponsorshipTemplateOptions.askQuestionProps?.isAnonymous,
                  onChange: (e) =>
                    setSponsorshipTemplateOptions((prev) => ({
                      ...prev,
                      linkText: e.target.checked
                        ? 'Ask a question anonymously'
                        : 'Ask a question here',
                      askQuestionProps: {
                        ...prev.askQuestionProps,
                        isAnonymous: e.target.checked,
                      },
                    })),
                }}
              />
            </div>
            <div
              className={classNames(
                css.settingsRow,
                css.sponsorshipSettingsRow
              )}
            >
              <Label1 className={css.tagSelectLabel}>
                Display link as button:
              </Label1>
              <ButtonCheckbox
                buttonProps={{ size: 'small' }}
                selectText=""
                unselectText=""
                inputProps={{
                  checked:
                    sponsorshipTemplateOptions.askQuestionProps?.isButton,
                  onChange: (e) =>
                    setSponsorshipTemplateOptions((prev) => ({
                      ...prev,
                      askQuestionProps: {
                        ...prev.askQuestionProps,
                        isButton: e.target.checked,
                      },
                    })),
                }}
              />
            </div>
          </>
        )}

      {sponsorshipReady && (
        <div
          className={classNames(css.settingsRow, css.sponsorshipSettingsRow)}
        >
          <TextInput
            showClear={false}
            className={classNames(css.input, css.sponsorshipLinkTextInput)}
            type="none"
            value={sponsorshipTemplateOptions.linkText}
            onChange={(newValue) =>
              setSponsorshipTemplateOptions((prev) => ({
                ...prev,
                linkText: newValue,
              }))
            }
            placeholder="Type link text here"
          />
        </div>
      )}
    </div>
  )
}
export const SponsorshipTemplateTypeSelect = ({
  selectedType,
  setSelectedType,
  cmeModuleAvailable,
}) => {
  const options = useMemo(() => {
    return Object.entries(SPONSORSHIP_LINK_TYPES_LABELS)
      .filter(
        ([key, _value]) => cmeModuleAvailable || key !== 'START_CME_PROGRAM'
      )
      .map(([key, value]) => ({
        label: value,
        value: key,
      }))
  }, [])

  useEffect(() => {
    if (!selectedType) {
      setSelectedType(Object.keys(SPONSORSHIP_LINK_TYPES_LABELS)[0])
    }
  }, [selectedType, setSelectedType])

  return (
    <div className={classNames(css.settingsRow, css.sponsorshipSettingsRow)}>
      <div>
        <ContainedSelect
          placeholder="Select Sponsorship (Program) Link Type"
          className={classNames(css.input, css.sponsorshipTypeInput)}
          options={options}
          onChange={(selected) => setSelectedType(selected.value)}
          showClear={false}
          value={options.find((option) => option.value === selectedType)}
        />
        <div className={css.sponsorshipTypeInputLabel}>
          <Subtitle3>
            If you want to view a specific question in the program use the
            question template and select to show the question in this program
          </Subtitle3>
        </div>
      </div>
    </div>
  )
}

export const SponsorshipTemplate = ({
  sponsorship,
  linkType,
  linkText,
  askQuestionProps,
}) => {
  const linkHref = useMemo(() => {
    if (!sponsorship) {
      return null
    }

    const baseUrl = `/v2/programs/${sponsorship?.sponsorshipId}`

    switch (linkType) {
      case SPONSORSHIP_LINK_TYPES.ASK_QUESTION:
        return getV2NotificationRedirectUrl(
          `${baseUrl}/ask?anonymous=${askQuestionProps.isAnonymous}#content`,
          V2_NOTIFICATION_REDIRECT_CLICK_TYPE.CAMPAIGN_ASK_PROGRAM_QUESTION,
          getClickedContentId(
            V2_NOTIFICATION_REDIRECT_CLICK_TYPE.CAMPAIGN_ASK_PROGRAM_QUESTION,
            { sponsorship }
          )
        )
      case SPONSORSHIP_LINK_TYPES.VIEW_PROGRAM:
        return getV2NotificationRedirectUrl(
          baseUrl,
          V2_NOTIFICATION_REDIRECT_CLICK_TYPE.CAMPAIGN_VIEW_PROGRAM,
          getClickedContentId(
            V2_NOTIFICATION_REDIRECT_CLICK_TYPE.CAMPAIGN_VIEW_PROGRAM,
            { sponsorship }
          )
        )

      case SPONSORSHIP_LINK_TYPES.VIEW_PROGRAM_ABSTRACTS:
        return getV2NotificationRedirectUrl(
          `${baseUrl}/abstract#content`,
          V2_NOTIFICATION_REDIRECT_CLICK_TYPE.CAMPAIGN_VIEW_PROGRAM_ABSTRACTS,
          getClickedContentId(
            V2_NOTIFICATION_REDIRECT_CLICK_TYPE.CAMPAIGN_VIEW_PROGRAM_ABSTRACTS,
            { sponsorship }
          )
        )
      case SPONSORSHIP_LINK_TYPES.VIEW_PROGRAM_VIDEOS:
        return getV2NotificationRedirectUrl(
          `${baseUrl}/video#content`,
          V2_NOTIFICATION_REDIRECT_CLICK_TYPE.CAMPAIGN_VIEW_PROGRAM_VIDEOS,
          getClickedContentId(
            V2_NOTIFICATION_REDIRECT_CLICK_TYPE.CAMPAIGN_VIEW_PROGRAM_VIDEOS,
            { sponsorship }
          )
        )
      case SPONSORSHIP_LINK_TYPES.START_CME_PROGRAM:
        return getV2NotificationRedirectUrl(
          `${baseUrl}/userCmeModule/${sponsorship?.cmeModule?.cmeModuleId}/redeemCme`,
          V2_NOTIFICATION_REDIRECT_CLICK_TYPE.CAMPAIGN_START_CME_PROGRAM,
          getClickedContentId(
            V2_NOTIFICATION_REDIRECT_CLICK_TYPE.CAMPAIGN_START_CME_PROGRAM,
            { sponsorship }
          )
        )
      default:
        return null
    }
  })

  if (
    linkType === SPONSORSHIP_LINK_TYPES.ASK_QUESTION &&
    askQuestionProps.isButton
  ) {
    return (
      <table width={250} height={40}>
        <tbody>
          <tr>
            <td
              style={{
                WebkitBorderRadius: '5px',
                MozBorderRadius: '5px',
                borderRadius: '5px',
                display: 'block',
                backgroundColor: '#006dcc',
                textAlign: 'center',
                lineHeight: '40px !important',
              }}
            >
              <a
                style={{
                  textDecoration: 'none',
                  color: '#ffffff',
                  width: '100%',
                  height: '100%',
                  display: 'inline-block',
                  lineHeight: '40px',
                  fontSize: '16px',
                  fontFamily: 'sans-serif',
                }}
                href={linkHref}
              >
                {linkText}
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    )
  }

  return <a href={linkHref}>{linkText}</a>
}
